/** Distância mínima entre o modal e as bordas da área visível. */
const MARGEM_DO_MODAL = 16;

/** Se a página pai não responder, mostra o diálogo na posição padrão depois desse tempo. */
export const ESPERA_MAXIMA_POSICAO_MS = 500;

/** Elemento do vivest-modal (Lit): `updateComplete` resolve depois que o componente renderiza. */
export type VivestModalElement = HTMLElement & { updateComplete?: Promise<unknown> };

function dialogDo(modal: VivestModalElement): HTMLDialogElement | null | undefined {
  return modal.shadowRoot?.querySelector('dialog');
}

/**
 * A página roda num iframe que o iframe-resizer estica até a altura do conteúdo, e o vivest-modal
 * (fixo) centraliza na "janela", que no iframe é a página inteira: numa página longa, o modal
 * aparece longe de onde a pessoa está. Aqui o diálogo vai para o meio da parte do iframe visível
 * na página pai, acompanhando a rolagem. Fora do iframe, o modal continua centralizado pela lib.
 * A posição chega da página pai por mensagem, depois de o diálogo abrir; até lá ele fica invisível,
 * para não aparecer na posição padrão e "descer" até o centro.
 *
 * Retorna a função que para de acompanhar a rolagem e devolve a posição padrão ao diálogo.
 */
export function centralizarModalNaAreaVisivel(modal: VivestModalElement): () => void {
  const parentIFrame = window.parentIFrame;
  if (!parentIFrame) return () => {};

  let ativo = true;
  let pararDeAcompanhar: (() => void) | undefined;
  let esperaMaxima: ReturnType<typeof setTimeout> | undefined;

  const mostrar = (dialog: HTMLDialogElement): void => {
    clearTimeout(esperaMaxima);
    dialog.style.removeProperty('visibility');
  };

  // O diálogo já existe no shadowRoot antes de abrir; escondido já aqui, não pisca na posição padrão.
  const dialogAtual = dialogDo(modal);
  if (dialogAtual) dialogAtual.style.visibility = 'hidden';

  void (async () => {
    await modal.updateComplete;
    const dialog = dialogDo(modal);
    if (!dialog || !ativo) return;

    dialog.style.visibility = 'hidden';
    esperaMaxima = setTimeout(() => mostrar(dialog), ESPERA_MAXIMA_POSICAO_MS);

    // Chamado de novo sempre que a página pai rola ou muda de tamanho.
    pararDeAcompanhar = parentIFrame.getParentProps(({ iframe, viewport }) => {
      const inicioVisivel = Math.max(0, -iframe.top);
      const fimVisivel = Math.min(iframe.height, viewport.height - iframe.top);
      const altura = dialog.offsetHeight;
      const topoMaximo = Math.max(MARGEM_DO_MODAL, iframe.height - altura - MARGEM_DO_MODAL);
      const topo = inicioVisivel + (fimVisivel - inicioVisivel - altura) / 2;

      dialog.style.insetBlock = `${Math.min(Math.max(topo, MARGEM_DO_MODAL), topoMaximo)}px auto`;
      dialog.style.marginBlock = '0';
      mostrar(dialog);
    });
  })();

  return () => {
    ativo = false;
    clearTimeout(esperaMaxima);
    pararDeAcompanhar?.();

    const dialog = dialogDo(modal);
    dialog?.style.removeProperty('inset-block');
    dialog?.style.removeProperty('margin-block');
    dialog?.style.removeProperty('visibility');
  };
}
